import React from 'react';

import { useIntl } from 'react-intl';

import { RESULTS_SORT_FIELDS } from '@atlassian/search-client/results-sort';

import { ProductKeys } from '../../../../../common/constants/product-keys';
import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter, {
	type SelectFilterOption,
} from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';
import { filter as sortFilter } from '../../../../../controllers/store/search/filters/sort';

import { messages } from './messages';

export const ResultsSort = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const [primaryProduct] = usePrimaryProduct();
	const [selectedProduct] = useSelectedProduct();

	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: sortFilter.id,
	});

	// also render in the All-apps view of Jira/Confluence FPS.
	const shouldRender =
		(!selectedProduct &&
			(primaryProduct === ProductKeys.Confluence || primaryProduct === ProductKeys.Jira)) ||
		selectedProduct === ProductKeys.Confluence ||
		selectedProduct === ProductKeys.Jira;

	if (!shouldRender) {
		return null;
	}

	if (!filterData) {
		return null;
	}

	const { isLoading, selectedOptions, options } = filterData;

	const selectedLabelKey = selectedOptionToSelectedLabelKey(selectedOptions[0]);
	const label = selectedLabelKey
		? formatMessage(messages[selectedLabelKey])
		: formatMessage(messages[RESULTS_SORT_FIELDS.relevanceDESC.selectedLabelKey]);

	return (
		<SelectFilter
			contentFit={fit}
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={options}
			selectedOptions={selectedOptions}
			label={label}
			filterOrControlId={sortFilter.id}
			isLoading={isLoading}
			isFilterInDropdown={isFilterInDropdown}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: sortFilter.id });
			}}
			getFirstSelectedLabel={() => ''}
		/>
	);
};

const selectedOptionToSelectedLabelKey = (option: SelectFilterOption) => {
	switch (option?.value) {
		case 'relevance':
			return RESULTS_SORT_FIELDS.relevanceDESC.selectedLabelKey;
		case 'updatedNewest':
			return RESULTS_SORT_FIELDS.latestModifiedDateDESC.selectedLabelKey;
		case 'updatedOldest':
			return RESULTS_SORT_FIELDS.latestModifiedDateASC.selectedLabelKey;
		case 'createdNewest':
			return RESULTS_SORT_FIELDS.createdDateDESC.selectedLabelKey;
		case 'createdOldest':
			return RESULTS_SORT_FIELDS.createdDateASC.selectedLabelKey;
	}
};
